/**
 * WALLET AND TREASURY. UF_03, expanded for the super-app (FR-U-WAL-001..008).
 *
 * The wallet answers three questions, and the second is the one most wallets get wrong:
 *
 *   What do I have?
 *   WHY CAN I NOT SPEND ALL OF IT?
 *   What has happened to it?
 *
 * Holdings are grouped the way the platform actually thinks about them: cash (fiat), virtual assets,
 * and tokenized real-world assets. Locked funds keep a section of their own with the plain-language
 * reason the backend supplies, because "insufficient balance" while the screen shows plenty is the
 * exact moment a wallet loses someone's trust.
 */

import { useCallback, useEffect, useState } from 'react';
import { fmtDateShort } from '@/lib/format';
import { formatWireAmount } from '@/lib/money';
import { copyText } from '@/lib/clipboard';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type Lock, type MyAsset, type Wallet } from '@/lib/api';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { StatusPill } from '@/components/status';
import { useSession } from '@/lib/session-context';

type History = Awaited<ReturnType<typeof api.history>>['transactions'];

const FIAT = new Set(['KES']);
const VIRTUAL = new Set(['BTC', 'ETH', 'USDT', 'USDC', 'SOL', 'XRP', 'BNB']);

const ASSET_META: Record<string, { name: string; tone: string }> = {
  KES: { name: 'Kenyan Shilling', tone: 'bg-primary/10 text-brand' },
  BTC: { name: 'Bitcoin', tone: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  ETH: { name: 'Ethereum', tone: 'bg-violet-500/15 text-violet-600 dark:text-violet-400' },
  USDT: { name: 'Tether', tone: 'bg-teal-500/15 text-teal-600 dark:text-teal-400' },
  USDC: { name: 'USD Coin', tone: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' },
  SOL: { name: 'Solana', tone: 'bg-lime-500/15 text-lime-600 dark:text-lime-400' },
  XRP: { name: 'XRP', tone: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  BNB: { name: 'BNB', tone: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
};

const CLASS_LABEL: Record<string, string> = {
  land: 'Land',
  real_estate: 'Property',
  vehicle: 'Vehicle',
  inventory: 'Inventory',
  receivable: 'Receivable',
};

export default function WalletPage() {
  const { me } = useSession();

  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [locks, setLocks] = useState<Lock[]>([]);
  const [history, setHistory] = useState<History>([]);
  const [assets, setAssets] = useState<MyAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!me) return;
    setError(null);
    try {
      const [w, l, h, a] = await Promise.all([
        api.wallet(me.participant_id),
        api.locks(me.participant_id),
        api.history(me.participant_id),
        api.myAssets().catch(() => []),
      ]);
      setWallet(w);
      setLocks(l.locks);
      setHistory(h.transactions.slice(0, 6));
      setAssets(a);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your wallet.');
    } finally {
      setLoading(false);
    }
  }, [me]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-40 animate-shimmer rounded-xl" />
        <LoadingCard lines={3} />
      </div>
    );
  }
  if (error) return <ErrorState body={error} onRetry={() => void load()} />;

  const balances = wallet?.balances ?? [];
  const fiat = balances.filter((b) => FIAT.has(b.asset));
  const virtual = balances.filter((b) => VIRTUAL.has(b.asset));
  const kes = fiat.find((b) => b.asset === 'KES');

  async function copyId() {
    const ok = await copyText(me?.participant_id ?? '');
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
      {/* MAIN */}
      <div className="space-y-6 lg:col-span-2">
        {/* VALUE HERO + quick actions */}
        <Reveal>
          <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-light/40 blur-2xl"
            />
            <div className="relative">
              <p className="text-sm text-white/70">Cash available to spend</p>
              <p className="mt-1 flex items-baseline gap-2">
                <span className="text-sm font-medium text-white/70">KES</span>
                <span className="tabular text-4xl font-semibold tracking-tight">
                  {formatWireAmount(kes?.available) || '0.00'}
                </span>
              </p>
              {kes && kes.locked !== '0.00' ? (
                <p className="mt-1 text-sm text-white/70">
                  KES {formatWireAmount(kes.locked)} held in escrow &middot; KES{' '}
                  {formatWireAmount(kes.total)} total
                </p>
              ) : null}

              <div className="mt-5 grid grid-cols-4 gap-2">
                <WalletAction href="/transact" label="Send" icon="M6 18 18 6m0 0h-8m8 0v8" />
                <WalletAction href="/receive" label="Receive" icon="M18 6 6 18m0 0h8m-8 0v-8" />
                <WalletAction href="/wallet/deposit" label="Add" icon="M12 5v14m-7-7h14" />
                <WalletAction
                  href="/wallet/withdraw"
                  label="Withdraw"
                  icon="M5 12h14m-7-7-7 7 7 7"
                />
              </div>
            </div>
          </section>
        </Reveal>

        {wallet?.frozen ? (
          <p className="rounded-card border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
            Your wallet is frozen while we complete a check. Your money is safe. We will be in
            touch.
          </p>
        ) : null}

        {/* CASH */}
        {fiat.length > 0 ? (
          <AssetGroup title="Cash">
            {fiat.map((b) => (
              <AssetRow key={b.asset} code={b.asset} available={b.available} locked={b.locked} />
            ))}
          </AssetGroup>
        ) : null}

        {/* VIRTUAL ASSETS */}
        {virtual.length > 0 ? (
          <AssetGroup title="Virtual assets">
            {virtual.map((b) => (
              <AssetRow key={b.asset} code={b.asset} available={b.available} locked={b.locked} />
            ))}
          </AssetGroup>
        ) : null}

        {/* TOKENIZED ASSETS */}
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="font-semibold text-ink">Tokenized assets</h2>
            <Link to="/tokenize" className="text-sm font-medium text-brand hover:underline">
              Manage
            </Link>
          </div>
          {assets.length === 0 ? (
            <Link
              to="/tokenize/new"
              className="flex items-center gap-3 rounded-card border border-dashed border-border-strong bg-surface p-4 transition hover:border-primary/40"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-brand">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.8}
                    d="M12 5v14m-7-7h14"
                  />
                </svg>
              </span>
              <span className="text-sm text-ink-muted">
                Turn property, land, or a vehicle into a token you can invest with or borrow
                against.
              </span>
            </Link>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
              {assets.map((a) => (
                <li key={a.asset_id}>
                  <Link
                    to={`/tokenize/assets/${a.asset_id}`}
                    className="flex items-center gap-3 px-4 py-3 transition hover:bg-surface-sunken"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M4 9.5 12 4l8 5.5M5.5 8.75V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8.75M9.5 20v-6h5v6"
                        />
                      </svg>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">
                        {CLASS_LABEL[a.class] ?? a.class}
                      </p>
                      <p className="truncate font-mono text-micro text-ink-subtle">
                        {a.ownership_reference}
                      </p>
                    </div>
                    <StatusPill status={a.verification_status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* RECENT ACTIVITY */}
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="font-semibold text-ink">Recent activity</h2>
            <Link to="/wallet" className="text-sm font-medium text-brand hover:underline">
              See all
            </Link>
          </div>
          {history.length === 0 ? (
            <EmptyState
              title="No transactions yet"
              body="Once money moves, every movement appears here, permanently."
            />
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
              {history.map((t) => {
                const incoming = t.direction === 'in';
                return (
                  <li key={t.id}>
                    <Link
                      to={`/activity/${t.correlationId}`}
                      className="flex items-center gap-3 px-4 py-3 transition hover:bg-surface-sunken"
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${incoming ? 'bg-accent/12 text-accent' : 'bg-primary/10 text-brand'}`}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d={incoming ? 'M18 6 6 18m0 0h8m-8 0v-8' : 'M6 18 18 6m0 0h-8m8 0v8'}
                          />
                        </svg>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-ink">
                          {incoming ? 'Received' : 'Sent'}
                        </p>
                        <p className="truncate text-xs text-ink-subtle">
                          {fmtDateShort(t.at)} &middot; {t.state}
                        </p>
                      </div>
                      <span
                        className={`tabular shrink-0 text-sm font-semibold ${incoming ? 'text-accent' : 'text-ink'}`}
                      >
                        {incoming ? '+' : '−'} {t.asset} {t.amount}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {/* RAIL: receive + locked */}
      <div className="space-y-6">
        <section className="rounded-card border border-border bg-surface p-5 shadow-card">
          <h2 className="font-semibold text-ink">Get paid</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Give this ID to whoever is paying you. They must be verified on VASP too.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-field bg-surface-sunken px-3 py-2 font-mono text-xs text-ink">
              {me?.participant_id}
            </code>
            <button
              onClick={copyId}
              className="shrink-0 rounded-field border border-border px-3 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </section>

        {locks.length > 0 ? (
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <h2 className="font-semibold text-ink">What is being held, and why</h2>
            <ul className="mt-3 space-y-3">
              {locks.map((l, i) => (
                <li key={i}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-ink">{l.reasonLabel}</span>
                    <span className="tabular shrink-0 text-sm font-medium text-ink">
                      {l.asset} {l.amount}
                    </span>
                  </div>
                  <p className="mt-0.5 font-mono text-micro text-ink-subtle">
                    {l.causedBy.kind} {l.causedBy.id}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function WalletAction({
  href,
  label,
  icon,
  onClick,
}: {
  href: string;
  label: string;
  icon: string;
  onClick?: () => void;
}) {
  return (
    <Link
      to={href}
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 rounded-lg bg-white/10 py-2.5 text-white backdrop-blur transition hover:bg-white/20"
    >
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={icon} />
      </svg>
      <span className="text-micro font-medium">{label}</span>
    </Link>
  );
}

function AssetGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 font-semibold text-ink">{title}</h2>
      <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
        {children}
      </ul>
    </section>
  );
}

function AssetRow({
  code,
  available,
  locked,
}: {
  code: string;
  available: string;
  locked: string;
}) {
  const meta = ASSET_META[code] ?? { name: code, tone: 'bg-surface-sunken text-ink-muted' };
  const hasLock = locked !== '0.00' && locked !== '0.000000' && locked !== '0.00000000';
  return (
    <li>
      <Link
        to={`/wallet/asset/${code}`}
        className="flex items-center gap-3 px-4 py-3 transition hover:bg-surface-sunken"
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-micro font-bold ${meta.tone}`}
        >
          {code.slice(0, 3)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink">{meta.name}</p>
          <p className="text-xs text-ink-subtle">
            {hasLock ? `${code} ${formatWireAmount(locked)} held` : code}
          </p>
        </div>
        <span className="tabular shrink-0 text-sm font-semibold text-ink">
          {formatWireAmount(available)}
        </span>
      </Link>
    </li>
  );
}
